import { Edit } from '@mui/icons-material';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf';
import SolarPowerIcon from '@mui/icons-material/SolarPower';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardStepper from '../components/dashboard/DashboardStepper';
import PlantSize from '../components/dashboard/PlantSize';
import SolarSearch from '../components/dashboard/SolarSearch';
import BenefitsSection from '../components/dashboard/BenefitsSection';
import EditPlantDetailsDialog from '../components/ui/EditPlantDetailsDialog';
import { DashboardLayout } from '../layoutV2/DashboardLayout';
import {
  generateRecommendations,
  getProposalsByUserId,
  getRecommendations,
  getUserProperties,
  updatePlantSize,
  updatePlantType,
  getCustomerDetails,
  getReferralCode,
} from '../services/CustomerService';
import { getUserDetails } from '../services/UserDetailsService';
import { usePageTitleStore } from '../store/PageTitleStore';
import { useSearchAnimate } from '../store/SearchAnimateStore';
import { InstallationTimeline } from '../utils/app.enums';
import { getProjectEstimate } from '../services/CustomerService';
import ShareButton from '../components/profile/profile-sections/ShareButton';
import { renderToStaticMarkup } from 'react-dom/server';
import ConnectWithoutContactRoundedIcon from '@mui/icons-material/ConnectWithoutContactRounded';
import offer_selection from '../config/offer_selection.json';
import PanelTechnologies from '../components/dashboard/PanelTechnologies';
import ValueAndPricing from '../components/dashboard/ValueAndPricing';
import { findStateDistrict } from '../services/CustomerService';
import ShareableApplication from '../components/dashboard/ShareableApplication';

const options = [
  {
    id: 1,
    title: 'Rooftop solar prices',
    name: '₹45,000 to 68,000 per kW',
    icon: (
      <CurrencyRupeeIcon
        fontSize='large'
        sx={{
          color: 'primary.main',
        }}
      />
    ),
    details: 'Depends on size, technology, brands, structure, warranties',
    href: 'https://www.vaysolar.com/docs/research/cost/',
    production: '10 units',
  },
  {
    id: 2,
    title: 'Benefits of solar',
    name: '₹2-3 per unit (up to 75% less)',
    icon: (
      <EnergySavingsLeafIcon
        fontSize='large'
        sx={{
          color: 'primary.main',
        }}
      />
    ),
    details:
      "You can reduce your electricity bills by up to 90%. Property's value also increases in the market!",
    href: 'https://www.vaysolar.com/docs/research/benefits/',
    production: '20 units',
  },
  {
    id: 3,
    title: 'How solar works?',
    name: 'Cells create energy from sunlight',
    icon: (
      <SolarPowerIcon
        fontSize='large'
        sx={{
          color: 'primary.main',
        }}
      />
    ),
    details: 'Solar energy is the cheapest form of energy source available',
    href: 'https://www.vaysolar.com/docs/research/how-it-works/',
    production: '40 units',
  },
];

const DashboardPage = () => {
  const { updateTitle, updateDescription } = usePageTitleStore();
  const [vendorData, setVendorData] = useState({});
  const [propertyDetails, setPropertyDetails] = useState(null);
  const [location, setLocation] = useState({ district: '', state: '' });
  const [proposals, setProposals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [installationTimeline, setInstallationTimeline] = useState('1 month');
  const { setIsSearchOver, setIsLooking } = useSearchAnimate();
  const [recommendations, setRecommendations] = useState([]);
  const [showPlantSizeSelection, setShowPlantSizeSelection] = useState(true);
  const [lookingForSolar, setLookingForSolar] = useState(true);
  const [userDetails, setUserDetails] = useState({});
  const [isPlantDetailsEditDialogOpen, setIsPlantDetailsEditDialogOpen] =
    useState(false);
  const [offersLoading, setOffersLoading] = useState(true);
  const [dataField, setDataField] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const watermarkSvgString = renderToStaticMarkup(
    <svg xmlns='http://www.w3.org/2000/svg' fill='#1B833312'>
      <ConnectWithoutContactRoundedIcon
        fontSize='inherit'
        sx={{
          width: '100%',
          height: '100%',
        }}
      />
    </svg>,
  );

  const getBenefitsData = async () => {
    try {
      if (!propertyDetails) return;

      const region =
        propertyDetails?.metadata?.state ||
        (await findStateDistrict(propertyDetails?.pinCode)) ||
        'Delhi';
      const sanctionedLoad = propertyDetails?.sanctionedLoad || 3;
      const propertyType = propertyDetails?.propertyType || 'RESIDENTIAL';
      const billAmount = propertyDetails?.billAmount || 2000;
      const billMonth = propertyDetails?.billMonth || 'JANUARY';
      const shadowFreeArea = propertyDetails?.shadowFreeArea || 0;
      const plantSize = propertyDetails?.plantSize || null;
      const data = await getProjectEstimate(
        region,
        propertyType,
        sanctionedLoad,
        billAmount,
        shadowFreeArea,
        billMonth,
        plantSize,
      );
      if (data.length === 0) throw new Error('No data found');
      setDataField(data);
    } catch (error) {
      console.log(error);
    }
  };

  const generatReferralCode = async () => {
    try {
      const { id } = getUserDetails();
      const res = await getReferralCode(id);
      window.location.reload();
      return res;
    } catch (error) {
      console.error('Error fetching referral code:', error);
      throw error;
    }
  };

  useEffect(() => {
    getBenefitsData();
  }, [propertyDetails]);

  useEffect(() => {
    if (userDetails) {
      const link = `https://www.vaysolar.com/en/getquote?fName=${encodeURIComponent(userDetails.fullName)}&r=${userDetails.referral_code}`;
      setReferralLink(link);
    }
  }, [userDetails]);

  const handleSupportButtonClick = () => {
    window.open('https://forms.gle/FYBfYapmfg77pZkz9', '_blank');
  };

  const handlePlantDetailsEditDialogOpen = () => {
    setIsPlantDetailsEditDialogOpen(true);
  };

  const handlePlantDetailsEditDialogClose = () => {
    setIsPlantDetailsEditDialogOpen(false);
  };

  const handlePlantDetailsEditDialogSave = async (plantDetails) => {
    setIsLoading(true);
    try {
      await updatePlantSize(propertyDetails?.id, plantDetails.plantSize);
      await updatePlantType(propertyDetails?.id, plantDetails.plantType);
      setPropertyDetails({
        ...propertyDetails,
        plantSize: plantDetails.plantSize,
        plantType: plantDetails.plantType,
      });
      setRecommendationsAndVendorsData(true);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const setRecommendationsAndVendorsData = async (
    refreshRecommendations = false,
  ) => {
    setOffersLoading(true);
    if (refreshRecommendations) {
      await generateRecommendations();
    }
    let recommendationsResponse = await getRecommendations();
    if (recommendationsResponse.length === 0) {
      await generateRecommendations();
      recommendationsResponse = await getRecommendations();
    }
    const recommendations = recommendationsResponse.filter(
      (recommendation) => recommendation.proposalConfig,
    );
    const vendorList = recommendationsResponse
      .filter((recommendation) => !recommendation.proposalConfig)
      .map((recommendation) => {
        return {
          ...recommendation?.vendor,
          recommendationId: recommendation.id,
        };
      });
    setVendorData(vendorList);
    setRecommendations(recommendations);
    setOffersLoading(false);
  };

  useEffect(() => {
    updateTitle('Dashboard | VaySolar');
    updateDescription(
      'VaySolar a platform for vendor and customer to unite world with solar power energy',
    );
  }, [updateDescription, updateTitle]);

  useEffect(() => {
    const onMount = async () => {
      try {
        const userDetails = getUserDetails();
        console.log('userDetails', userDetails);

        const customerDetails = await getCustomerDetails(userDetails.id);
        console.log('customerDetails', customerDetails);

        setUserDetails(customerDetails);
        const data = await getUserProperties();
        setPropertyDetails(data[0]);
        setInstallationTimeline(data[0]?.installationTimeline);
        if (
          data[0]?.installationTimeline === InstallationTimeline.NOT_LOOKING
        ) {
          setIsSearchOver(false);
          setIsLooking(false);
          setLookingForSolar(false);
        }
        if (data[0]?.plantSize) {
          setShowPlantSizeSelection(false);
        }
        if (
          data[0]?.plantSize &&
          data[0]?.installationTimeline !== InstallationTimeline.NOT_LOOKING
        ) {
          const proposalsResponse = await getProposalsByUserId();
          setProposals(proposalsResponse);
          await setRecommendationsAndVendorsData();
          if (data[0]?.pinCode) {
            const response = await fetch(
              `https://api.postalpincode.in/pincode/${data[0].pinCode}`,
            );
            const pinCodeResponse = await response.json();
            if (pinCodeResponse[0].Status === 'Success') {
              const { District, State } = pinCodeResponse[0].PostOffice[0];
              setLocation({ district: District, state: State });
            }
          }
        }
      } catch (error) {
      } finally {
        setIsLoading(false);
        setOffersLoading(false);
      }
    };
    onMount();
  }, []);

  if (isLoading || offersLoading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        height='100vh'
        sx={{
          flexGrow: 1,
          px: { sm: 3, xs: 1.5 },
          py: { sm: 3, xs: 2 },
          pb: { sm: 3, xs: 8 },
        }}
      >
        <CircularProgress color='primary' />
      </Box>
    );
  }

  const DashboardMainContent = () => {
    return (
      <>
        {propertyDetails?.plantSize && !offersLoading && (
          <Box mb={4}>
            <Stack
              direction={'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
            >
              <Stack direction={'row'} mt={2} mb={0.5} alignItems={'center'}>
                <Typography variant={'h5'}>Plant size &nbsp;</Typography>
                <Typography variant={'h5'} fontWeight={'bold'}>
                  {propertyDetails?.plantSize}kW{' '}
                  {propertyDetails?.plantType?.toLowerCase()}
                </Typography>
                <IconButton onClick={handlePlantDetailsEditDialogOpen}>
                  <Edit />
                </IconButton>
              </Stack>
            </Stack>
            {userDetails?.metadata?.passkey &&
              userDetails?.metadata?.applicationId && (
                <ShareableApplication
                  passkey={userDetails?.metadata?.passkey}
                  decodedId={userDetails?.metadata?.applicationId}
                />
              )}
            <BenefitsSection dataField={dataField} />
          </Box>
        )}
        {propertyDetails?.plantSize &&
          userDetails?.metadata?.applicationId == null && (
            <ValueAndPricing
              dataField={dataField}
              propertyDetails={propertyDetails}
              userDetails={userDetails}
              recommendations={recommendations}
            />
          )}
        {dataField?.input?.region === 'Delhi' && (
          <PanelTechnologies
            offer_selection={offer_selection}
            propertyDetails={propertyDetails}
            userDetails={userDetails}
            recommendations={recommendations}
          />
        )}
        {userDetails?.metadata?.applicationId == null && (
          <DashboardStepper
            proposals={proposals}
            propertyDetails={propertyDetails}
            vendorData={vendorData}
            recommendations={recommendations}
            userDetails={userDetails}
            district={location.district}
            state={location.state}
          />
        )}
        <EditPlantDetailsDialog
          open={isPlantDetailsEditDialogOpen}
          handleClose={handlePlantDetailsEditDialogClose}
          propertyDetails={propertyDetails}
          handleSave={handlePlantDetailsEditDialogSave}
        />
      </>
    );
  };

  return (
    <DashboardLayout
      announcementBar={true}
      propertyDetails={propertyDetails}
      installationTimeline={installationTimeline}
    >
      <Container
        component='main'
        sx={{
          flexGrow: 1,
          pb: 2,
          paddingInline: { md: '24px', xs: '12px' },
          paddingTop: '13px',
        }}
      >
        {!lookingForSolar ? (
          <SolarSearch propertyDetails={propertyDetails} />
        ) : showPlantSizeSelection ? (
          <PlantSize propertyDetails={propertyDetails} />
        ) : (
          DashboardMainContent()
        )}
        <Stack alignItems='flex-start' mt={6}>
          <Typography
            sx={{
              fontSize: {
                xs: theme.typography.h5.fontSize,
                md: theme.typography.h4.fontSize,
              },
              fontWeight: 'bold',
            }}
          >
            All questions answered
          </Typography>
          <Stack
            alignItems={isMobile ? 'flex-start' : 'unset'}
            mt={3}
            flexDirection={isMobile ? 'column' : 'row'}
            width={'100%'}
            gap={isMobile ? '8px' : '12px'}
          >
            {options.map((option, index) => (
              <Card
                key={option.id}
                sx={{
                  cursor: 'auto',
                  width: '100%',
                  marginTop: 2,
                  borderRadius: '16px',
                  border: '1px solid lightgray',
                  boxShadow: 'none',
                  maxWidth: '500px',
                }}
              >
                <CardContent
                  sx={{
                    display: 'flex',
                    flex: 1,
                    height: '100%',
                  }}
                >
                  <Stack>
                    <Typography
                      sx={{
                        fontSize: {
                          xs: theme.typography.h5.fontSize,
                          md: theme.typography.h5.fontSize,
                        },
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        lineHeight: '3rem',
                      }}
                      mb={isMobile ? 1 : 2}
                    >
                      <Typography component={'span'}>{option.icon}</Typography>
                      <Typography
                        component={'span'}
                        pl={option.id === 1 ? 1 : 1.5}
                        fontWeight={'bold'}
                        fontSize={'18px'}
                      >
                        {option.title}
                      </Typography>
                    </Typography>
                    <Stack
                      flexGrow={1}
                      gap={'24px'}
                      justifyContent={'space-between'}
                    >
                      <Typography
                        sx={{
                          color: theme.palette.secondary.light,
                          fontSize: {
                            xs: theme.typography.h6.fontSize,
                            md: theme.typography.h6.fontSize,
                          },
                          fontWeight: 'bold',
                        }}
                      >
                        {option.name}
                        <br />
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: {
                            xs: theme.typography.body2.fontSize,
                            md: theme.typography.body2.fontSize,
                          },
                          color: 'rgba(0, 0, 0, 0.38)',
                        }}
                      >
                        {option.details}
                      </Typography>
                    </Stack>
                    <Typography alignSelf={'flex-end'} py={0.5}>
                      <Link href={option.href} variant='body2' target='_blank'>
                        read more
                      </Link>
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
          <Typography
            mt={6}
            sx={{
              fontSize: theme.typography.body2.fontSize,
              color: theme.palette.primary.main,
            }}
          >
            Not sure still?{' '}
            <Link
              onClick={() => {
                window.location.href =
                  'https://www.vaysolar.com/docs/?utm_source=landing-page&utm_medium=landing-page&utm_campaign=landing-page';
              }}
            >
              Here is a guide on how to buy solar
            </Link>
            <br />
            Unsure still?{' '}
            <Link onClick={handleSupportButtonClick}>Talk to an expert</Link>
            <br />
          </Typography>
        </Stack>
      </Container>
      <Box
        sx={{
          mt: 8,
          mb: 2,
          padding: isMobile ? 2 : 3,
          height: '70vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: isMobile ? '100%' : '65%',
            backgroundImage: `url('data:image/svg+xml;utf8,${encodeURIComponent(
              watermarkSvgString,
            )}')`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: { md: '65%', xs: '100%' },
            backgroundPosition: isMobile ? 'center' : 'right top',
            pointerEvents: 'none',
          }}
        />

        <Grid sx={{ width: '100%', pr: isMobile ? 0 : '10rem' }}>
          <Typography
            sx={{
              fontFamily: 'Plus Jakarta Sans',
              fontWeight: 700,
              letterSpacing: '-0.5px',
              WebkitTextStroke: isMobile ? '0.6px black' : '1.4px black',
              color: '#000',
            }}
            variant={isMobile ? 'h3' : 'h1'}
            gutterBottom
          >
            Build your solar network, earn cash rewards{' '}
          </Typography>
          <Typography variant='body1' color='black' fontWeight={600} pb={2}>
            <br /> Share your unique referral with friends
            <br />
            They get rewards upto ₹15,000 on their installation
            <br />
            You earn ₹3,000 to ₹7,000 on each successful referral{' '}
          </Typography>
          <Box
            display='flex'
            gap={2}
            flexDirection={isMobile ? 'column' : 'row'}
            width={isMobile ? '100%' : 'auto'}
            justifyContent={isMobile ? 'center' : 'flex-start'}
          >
            <ShareButton
              shareLink={`Hey! Main apna solar network VaySolar pe build kar raha hoon ☀
                      
Agar tum mere link se join karte ho, toh tumhe milega:
✅ ₹5000 tak ka discount on your solar system
✅ 20 cleaning visits worth ₹10,000+
✅ Free solar savings check (sirf 30 seconds mein)

Join karo aur solar mein smart investment ka pehla step lo 💡
${referralLink}`}
              shareprofile
              onClick={
                !userDetails?.referral_code ? generatReferralCode : undefined
              }
            />
          </Box>
        </Grid>
      </Box>
    </DashboardLayout>
  );
};

export default DashboardPage;
